import type { GoalDriftSummary, GoalItem, LifeAllocation } from "@/lib/api"

export const MAX_PLAN_SUPPORT_GOALS = 3

export type PlanSupportGoalReason = "focused" | "recovery" | "protected" | "steady" | "supporting"

export interface PlanSupportGoal {
  goal: GoalItem
  reason: PlanSupportGoalReason
}

function sortFallbackGoals(goals: GoalItem[]): GoalItem[] {
  return [...goals].sort((left, right) => {
    const progressDelta = right.progress_pct - left.progress_pct
    if (Math.abs(progressDelta) > 0.01) return progressDelta

    const milestoneDelta = right.milestones.length - left.milestones.length
    if (milestoneDelta !== 0) return milestoneDelta

    return new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
  })
}

export function buildPlanSupportGoals({
  goals,
  goalDriftSummary,
  focusGoalId,
  lifeAllocation,
}: {
  goals: GoalItem[]
  goalDriftSummary: GoalDriftSummary | null
  focusGoalId: string | null
  lifeAllocation?: LifeAllocation | null
}) {
  const goalById = new Map(goals.map((goal) => [goal.id, goal]))
  const ranked: Array<{ id: string; reason: PlanSupportGoalReason }> = []

  if (focusGoalId && goalById.has(focusGoalId)) {
    ranked.push({ id: focusGoalId, reason: "focused" })
  }

  for (const goal of goalDriftSummary?.top_drifting_goals ?? []) {
    if (goalById.has(goal.id)) {
      ranked.push({ id: goal.id, reason: "recovery" })
    }
  }

  for (const goal of lifeAllocation?.goals ?? []) {
    if (goal.allocation_mode === "protect" && goalById.has(goal.id)) {
      ranked.push({ id: goal.id, reason: "protected" })
    }
  }

  for (const goal of goalDriftSummary?.top_steady_goals ?? []) {
    if (goalById.has(goal.id)) {
      ranked.push({ id: goal.id, reason: "steady" })
    }
  }

  for (const goal of sortFallbackGoals(goals)) {
    ranked.push({ id: goal.id, reason: "supporting" })
  }

  const seen = new Set<string>()
  const visibleSupportGoals: PlanSupportGoal[] = []

  for (const entry of ranked) {
    if (seen.has(entry.id)) continue
    const goal = goalById.get(entry.id)
    if (!goal) continue
    seen.add(entry.id)
    visibleSupportGoals.push({
      goal,
      reason: entry.reason,
    })
    if (visibleSupportGoals.length >= MAX_PLAN_SUPPORT_GOALS) break
  }

  return {
    visibleSupportGoals,
    hiddenGoalCount: Math.max(goals.length - visibleSupportGoals.length, 0),
  }
}
